import React, { useEffect, useState } from 'react';
import AuthedImage from '../components/AuthedImage';
import { buildMinioProxyUrl, openWithAuth } from '../utils/authedResource';
import { useAuth } from '../context/AuthContext';
import { Card, Form, Input, Button, Space, Table, Tag, Modal, Upload, message, Select, Radio, Pagination } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import axios from '../utils/http';

export default function WorkNotesPage(){
  const { user, hasPermission } = useAuth();
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [queryForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [attachments, setAttachments] = useState([]); // 当前分页附件
  const [attachmentTotal, setAttachmentTotal] = useState(0);
  const [attPage, setAttPage] = useState(1);
  const [attPageSize, setAttPageSize] = useState(12);
  const [attType, setAttType] = useState('all'); // all | image | nonimage
  const [preUploadFiles, setPreUploadFiles] = useState([]); // 创建模式下暂存
  const [uploading, setUploading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [attLoading, setAttLoading] = useState(false);

  const fetchList = async () => {
    setListLoading(true);
    try {
      const { q, tag } = queryForm.getFieldsValue();
      const params = { page, pageSize };
      if (q) params.q = q;
      if (tag) params.tag = tag;
      const res = await axios.get('/work-notes', { params });
      setData(res.data.data||[]); setTotal(res.data.total||0);
    } catch(e){ message.error(e?.message||'加载失败'); }
    setListLoading(false);
  };

  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`/work-notes/${id}`);
      const note = res.data.data;
      editForm.setFieldsValue({
        title: note.title,
        content: note.content,
        tags: (note.tags ? note.tags.split(',').filter(Boolean): [])
      });
      setAttachmentTotal(res.data.attachmentCount||0);
      setEditingId(id);
      setShowEdit(true);
      // 加载第一页附件
      fetchAttachments(id, 1, attPageSize, attType);
    } catch(e){ message.error(e?.message||'获取详情失败'); }
    setDetailLoading(false);
  };

  const resetEdit = (keepOpen=false) => {
    setEditingId(null); editForm.resetFields(); setAttachments([]); setAttachmentTotal(0); setAttPage(1); setAttType('all');
    if (!keepOpen) setShowEdit(false);
  };

  const submitNote = async () => {
    try {
      const values = await editForm.validateFields();
      if (editingId){
        const payload = { ...values, tags: values.tags||[] };
        await axios.put(`/work-notes/${editingId}`, payload);
        message.success('更新成功');
      } else {
        const fd = new FormData();
        fd.append('title', values.title);
        fd.append('content', values.content||'');
        (values.tags||[]).forEach(t=> fd.append('tags', t));
        preUploadFiles.forEach(f=> {
          const fileObj = f.originFileObj || f; // fallback if originFileObj missing
            fd.append('files', fileObj, fileObj.name || f.name);
        });
        await axios.post('/work-notes', fd, { headers:{'Content-Type':'multipart/form-data'} });
        message.success('创建成功');
      }
      fetchList(); setPreUploadFiles([]); resetEdit(false);
    } catch(e){ if (e?.errorFields) return; message.error(e?.message||'提交失败'); }
  };

  const deleteNote = async (id) => {
    Modal.confirm({ title:'确认删除该笔记？', onOk: async ()=>{
      try { await axios.delete(`/work-notes/${id}`); message.success('已删除'); fetchList(); if (editingId===id) { setShowEdit(false); resetEdit(); } } catch(e){ message.error(e?.message||'删除失败'); }
    }});
  };

  const allowedTypes = ['image/','application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','application/zip'];
  const maxSize = 20 * 1024 * 1024; // 20MB 前端限制
  const validateFile = (file) => {
    if (file.size > maxSize){ message.error(`文件 ${file.name} 超过 20MB 限制`); return false; }
    // 图片统一前缀 image/, 其它匹配数组
    const mime = file.type;
    if (mime.startsWith('image/')) return true;
    if (!allowedTypes.includes(mime)) { message.error(`不支持的文件类型: ${file.name}`); return false; }
    return true;
  };

  const handleUpload = async ({file}) => {
    if (!validateFile(file)) return;
    if (!editingId){
      // 创建模式，暂存到 preUploadFiles
      setPreUploadFiles(prev=> [...prev, {...file, uid: file.uid||file.name}]);
      message.success('已添加待上传文件');
      return;
    }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`/work-notes/${editingId}/attachments`, fd, { headers:{'Content-Type':'multipart/form-data'} });
      message.success('上传成功');
      fetchAttachments(editingId, attPage, attPageSize, attType);
    } catch(e){ message.error(e?.message||'上传失败'); }
    setUploading(false);
  };

  const deleteAttachment = async (att) => {
    Modal.confirm({ title:'删除附件?', onOk: async ()=>{
      try { await axios.delete(`/work-notes/attachments/${att.id}`); message.success('附件删除'); loadDetail(editingId); } catch(e){ message.error(e?.message||'删除失败'); }
    }});
  };

  const fetchAttachments = async (noteId, p=1, ps=attPageSize, type=attType) => {
    if (!noteId) return;
    setAttLoading(true);
    try {
      const params = { page:p, pageSize:ps };
      if (type && type!=='all') params.type = type;
      const res = await axios.get(`/work-notes/${noteId}/attachments`, { params });
      setAttachments(res.data.data||[]);
      setAttachmentTotal(res.data.total||0);
      setAttPage(p); setAttPageSize(ps);
    } catch(e){ /* ignore */ }
    setAttLoading(false);
  };

  useEffect(()=>{ fetchList(); },[page,pageSize]);

  const canManageAll = hasPermission && hasPermission('manage_work_notes');
  const canManageOwn = hasPermission && hasPermission('manage_own_work_notes');
  const columns = [
    { title:'标题', dataIndex:'title', width:260, render:(v,r)=>(<a onClick={()=>loadDetail(r.id)}>{v}</a>) },
    { title:'标签', dataIndex:'tags', width:220, render:v=> (v? v.split(',').filter(Boolean).map(t=> <Tag key={t}>{t}</Tag>): null) },
    { title:'更新时间', dataIndex:'updated_at', width:160, render:v=> v? v.replace('T',' ').replace('Z',''):'' },
    { title:'操作', width:120, render:(_,r)=>{
      // 只有有权限且是本人笔记或管理员才显示编辑/删除
      const isOwner = user && r.created_by === user.username;
      if (canManageAll || (canManageOwn && isOwner)) {
        return (
          <Space>
            <Button size='small' onClick={()=>loadDetail(r.id)}>编辑</Button>
            <Button danger size='small' onClick={()=>deleteNote(r.id)}>删除</Button>
          </Space>
        );
      }
      return null;
    } }
  ];

  return (
    <div style={{padding:16}}>
      <h2 style={{marginBottom:16}}>工作笔记</h2>
      <Card size='small' style={{marginBottom:16}}>
        <Form form={queryForm} layout='inline'>
          <Form.Item name='q' label='关键词'>
            <Input placeholder='标题或内容关键字' allowClear />
          </Form.Item>
          <Form.Item name='tag' label='标签'>
            <Input placeholder='标签' allowClear />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type='primary' onClick={()=>{ setPage(1); fetchList(); }}>查询</Button>
              <Button onClick={()=>{ queryForm.resetFields(); setPage(1); fetchList(); }}>重置</Button>
              {(canManageAll || canManageOwn) && <Button onClick={()=>{ setShowEdit(true); resetEdit(true); }}>新建笔记</Button>}
            </Space>
          </Form.Item>
        </Form>
        {(!canManageAll && !canManageOwn) && <div style={{color:'#b91c1c',marginTop:8}}>你没有创建或管理工作笔记的权限，如有需要请联系管理员。</div>}
      </Card>
      <Card size='small' style={{marginBottom:24}}>
        <Table rowKey='id' size='small' loading={listLoading} dataSource={data} columns={columns} pagination={{current:page,pageSize,total,onChange:(p,ps)=>{setPage(p);setPageSize(ps);}}}
          locale={{emptyText: (!canManageAll && !canManageOwn) ? '无权限查看笔记' : '暂无数据'}}
        />
      </Card>

      <Modal open={showEdit} onCancel={()=>{ setShowEdit(false); }} width={860} title={editingId? '编辑笔记':'新建笔记'} footer={null}>
        <Form form={editForm} layout='vertical'>
          <Form.Item name='title' label='标题' rules={[{required:true,message:'请输入标题'}]}>
            <Input placeholder='标题' />
          </Form.Item>
          <Form.Item name='tags' label='标签'>
            <Select mode='tags' placeholder='输入或选择标签' open={false} />
          </Form.Item>
          <Form.Item name='content' label='内容'>
            <Input.TextArea rows={10} placeholder='内容，可支持简单文字记录' />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type='primary' onClick={submitNote}>{editingId? '保存':'创建'}</Button>
              <Button onClick={()=>{ setShowEdit(false); }}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
        <Card size='small' title='附件' style={{marginTop:16}} extra={editingId? (<Radio.Group size='small' value={attType} onChange={e=>{ setAttType(e.target.value); fetchAttachments(editingId,1,attPageSize,e.target.value); }}>
          <Radio.Button value='all'>全部</Radio.Button>
          <Radio.Button value='image'>图片</Radio.Button>
          <Radio.Button value='nonimage'>其它</Radio.Button>
        </Radio.Group>): null}>
          {editingId ? (
            <Upload.Dragger multiple showUploadList={false} customRequest={({file,onSuccess})=>{ handleUpload({file}); onSuccess('ok'); }} accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip' disabled={uploading} style={{padding:8}}>
              <p className='ant-upload-drag-icon'><UploadOutlined /></p>
              <p style={{margin:0,fontSize:13}}>拖拽或点击选择文件（支持批量）</p>
              <p style={{margin:0,fontSize:12,color:'#64748b'}}>限制 20MB，类型：图片 / PDF / Word / Excel / ZIP</p>
            </Upload.Dragger>
          ) : (
            <Upload multiple showUploadList fileList={preUploadFiles} onRemove={file=>{ setPreUploadFiles(preUploadFiles.filter(f=> f.uid!==file.uid)); }} customRequest={({file,onSuccess})=>{ handleUpload({file}); onSuccess('ok'); }} accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip'>
              <Button icon={<UploadOutlined />} loading={uploading}>添加附件</Button>
            </Upload>
          )}
          {!editingId && preUploadFiles.length>0 && <div style={{marginTop:8, fontSize:12, color:'#64748b'}}>将随创建一并上传 {preUploadFiles.length} 个文件</div>}
          {editingId && (
            <div style={{marginTop:12}}>
              {attachments.length===0 && !attLoading && <div style={{color:'#94a3b8'}}>暂无附件</div>}
              <div style={{display:'flex', flexWrap:'wrap', gap:12}}>
                {attachments.map(att => (
                  <div key={att.id} style={{width:150, border:'1px solid #e2e8f0', borderRadius:6, padding:6}}>
                    <div style={{fontSize:12, fontWeight:600}} title={att.file_name}>{att.file_name}</div>
                    <div style={{fontSize:11, color:'#64748b'}}>{Math.round((att.file_size||0)/1024)} KB</div>
                    {att.mime_type && att.mime_type.startsWith('image/') && (
                      <div style={{marginTop:6}}>
                        <AuthedImage src={buildMinioProxyUrl(att.object_name || att.objectName) || att.url} alt={att.file_name} style={{maxWidth:'100%', maxHeight:80, objectFit:'cover'}} />
                      </div>
                    )}
                    <Space style={{marginTop:6}}>
                      <Button size='small' type='link' onClick={() => openWithAuth(att.url)}>预览</Button>
                      <Button size='small' danger onClick={()=>deleteAttachment(att)}>删除</Button>
                    </Space>
                  </div>
                ))}
              </div>
              <div style={{marginTop:12, textAlign:'right'}}>
                <Pagination size='small' showSizeChanger current={attPage} pageSize={attPageSize} total={attachmentTotal} onChange={(p,ps)=> fetchAttachments(editingId,p,ps,attType)} />
              </div>
            </div>
          )}
        </Card>
      </Modal>
    </div>
  );
}
